import type { Statement, TriggerExpr } from './flowModel';
import { parseFlow } from './flowParser';

export type BusinessSequenceSourceState =
  | 'derived'
  | 'edited'
  | 'manual'
  | 'split'
  | 'merged'
  | 'orphaned';

export type BusinessSequenceEvidenceState = 'available' | 'missing';

export interface EvidenceReference {
  id: string;
  statementId: number | null;
  summary: string;
  source: string;
}

export interface BusinessSequenceStep {
  id: string;
  label: string;
  order: number;
  evidenceRefs: EvidenceReference[];
  evidenceState: BusinessSequenceEvidenceState;
  sourceState: BusinessSequenceSourceState;
  sourceStepIds: string[];
}

export interface SequenceOverlayStep {
  id: string;
  label: string;
  evidenceRefs: EvidenceReference[];
  sourceState: Extract<BusinessSequenceSourceState, 'manual' | 'split' | 'merged'>;
  sourceStepIds: string[];
}

export type SequenceOperation =
  | {
      type: 'move';
      stepId: string;
      toIndex?: number;
      beforeStepId?: string;
      afterStepId?: string;
    }
  | { type: 'rename'; stepId: string; label: string }
  | { type: 'add'; label: string; beforeStepId?: string; afterStepId?: string }
  | { type: 'split'; stepId: string; labels: [string, string] }
  | { type: 'merge'; stepId: string; withStepId: string; label?: string };

export type SequenceOperationDraft = SequenceOperation & {
  origin?: 'ui' | 'chat';
};

export interface StreamBusinessSequenceOverlay {
  version: 1;
  orderedStepIds: string[];
  labelOverrides: Record<string, string>;
  hiddenStepIds: string[];
  manualSteps: Record<string, SequenceOverlayStep>;
  operationHistory: SequenceOperation[];
}

export function createEmptySequenceOverlay(): StreamBusinessSequenceOverlay {
  return {
    version: 1,
    orderedStepIds: [],
    labelOverrides: {},
    hiddenStepIds: [],
    manualSteps: {},
    operationHistory: [],
  };
}

export function normalizeSequenceOverlay(input: unknown): StreamBusinessSequenceOverlay {
  if (!input || typeof input !== 'object') return createEmptySequenceOverlay();
  const raw = input as Partial<StreamBusinessSequenceOverlay>;
  const empty = createEmptySequenceOverlay();

  return {
    version: 1,
    orderedStepIds: Array.isArray(raw.orderedStepIds)
      ? raw.orderedStepIds.filter(isNonEmptyString)
      : empty.orderedStepIds,
    labelOverrides: normalizeStringRecord(raw.labelOverrides),
    hiddenStepIds: Array.isArray(raw.hiddenStepIds)
      ? raw.hiddenStepIds.filter(isNonEmptyString)
      : empty.hiddenStepIds,
    manualSteps: normalizeOverlaySteps(raw.manualSteps),
    operationHistory: Array.isArray(raw.operationHistory)
      ? raw.operationHistory.map(normalizeSequenceOperation)
      : empty.operationHistory,
  };
}

export function normalizeSequenceOperation(operation: SequenceOperationDraft): SequenceOperation {
  switch (operation.type) {
    case 'move':
      return {
        type: 'move',
        stepId: operation.stepId,
        ...(typeof operation.toIndex === 'number' ? { toIndex: operation.toIndex } : {}),
        ...(operation.beforeStepId ? { beforeStepId: operation.beforeStepId } : {}),
        ...(operation.afterStepId ? { afterStepId: operation.afterStepId } : {}),
      };
    case 'rename':
      return {
        type: 'rename',
        stepId: operation.stepId,
        label: cleanLabel(operation.label),
      };
    case 'add':
      return {
        type: 'add',
        label: cleanLabel(operation.label),
        ...(operation.beforeStepId ? { beforeStepId: operation.beforeStepId } : {}),
        ...(operation.afterStepId ? { afterStepId: operation.afterStepId } : {}),
      };
    case 'split':
      return {
        type: 'split',
        stepId: operation.stepId,
        labels: [cleanLabel(operation.labels[0]), cleanLabel(operation.labels[1])],
      };
    case 'merge':
      return {
        type: 'merge',
        stepId: operation.stepId,
        withStepId: operation.withStepId,
        ...(operation.label ? { label: cleanLabel(operation.label) } : {}),
      };
    default:
      return operation;
  }
}

export function deriveBusinessSequenceFromFlow(flowText: string): BusinessSequenceStep[] {
  const parsed = parseFlow(flowText || '');
  const statements = parsed.statements.slice().sort((a, b) => a.id - b.id);
  const primaryStatements = statements.filter((statement) => statement.type !== 'event');

  return primaryStatements.map((statement, index) => {
    const evidenceRefs = deriveEvidenceRefs(statement, statements);
    return {
      id: stepIdForStatement(statement.id),
      label: humanizeActivity(statement.name),
      order: index + 1,
      evidenceRefs,
      evidenceState: evidenceRefs.length > 0 ? 'available' : 'missing',
      sourceState: 'derived',
      sourceStepIds: [stepIdForStatement(statement.id)],
    };
  });
}

export function resolveBusinessSequence(input: {
  flowText: string;
  overlay?: unknown;
}): BusinessSequenceStep[] {
  return resolveBusinessSequenceFromSteps(
    deriveBusinessSequenceFromFlow(input.flowText),
    input.overlay,
  );
}

export function applySequenceOperation(
  overlayInput: unknown,
  operationInput: SequenceOperationDraft,
  derivedSteps: BusinessSequenceStep[],
): StreamBusinessSequenceOverlay {
  const overlay = normalizeSequenceOverlay(overlayInput);
  const operation = normalizeSequenceOperation(operationInput);
  const currentSteps = resolveBusinessSequenceFromSteps(derivedSteps, overlay);
  const currentIds = currentSteps.map((step) => step.id);
  const currentById = new Map(currentSteps.map((step) => [step.id, step]));

  switch (operation.type) {
    case 'move': {
      if (!currentById.has(operation.stepId)) return overlay;
      const withoutMoved = currentIds.filter((id) => id !== operation.stepId);
      const insertIndex = resolveInsertIndex(operation, withoutMoved);
      return withHistory(
        {
          ...cloneOverlay(overlay),
          orderedStepIds: insertAt(withoutMoved, operation.stepId, insertIndex),
        },
        operation,
      );
    }

    case 'rename': {
      if (!operation.label || !currentById.has(operation.stepId)) return overlay;
      const next = cloneOverlay(overlay);
      next.labelOverrides[operation.stepId] = operation.label;
      if (next.manualSteps[operation.stepId]) {
        next.manualSteps[operation.stepId] = {
          ...next.manualSteps[operation.stepId],
          label: operation.label,
        };
      }
      if (next.orderedStepIds.length === 0) next.orderedStepIds = currentIds;
      return withHistory(next, operation);
    }

    case 'add': {
      if (!operation.label) return overlay;
      const next = cloneOverlay(overlay);
      const stepId = uniqueStepId(`manual-${slugify(operation.label) || 'step'}`, [
        ...currentIds,
        ...Object.keys(next.manualSteps),
      ]);
      next.manualSteps[stepId] = {
        id: stepId,
        label: operation.label,
        evidenceRefs: [],
        sourceState: 'manual',
        sourceStepIds: [],
      };
      const insertIndex = resolveInsertIndex(operation, currentIds);
      next.orderedStepIds = insertAt(currentIds, stepId, insertIndex);
      return withHistory(next, operation);
    }

    case 'split': {
      const step = currentById.get(operation.stepId);
      const [firstLabel, secondLabel] = operation.labels;
      if (!step || !firstLabel || !secondLabel) return overlay;
      const next = cloneOverlay(overlay);
      const firstId = uniqueStepId(`split-${operation.stepId}-1`, [
        ...currentIds,
        ...Object.keys(next.manualSteps),
      ]);
      const secondId = uniqueStepId(`split-${operation.stepId}-2`, [
        ...currentIds,
        ...Object.keys(next.manualSteps),
        firstId,
      ]);
      next.hiddenStepIds = addUnique(next.hiddenStepIds, operation.stepId);
      next.manualSteps[firstId] = {
        id: firstId,
        label: firstLabel,
        evidenceRefs: step.evidenceRefs,
        sourceState: 'split',
        sourceStepIds: step.sourceStepIds,
      };
      next.manualSteps[secondId] = {
        id: secondId,
        label: secondLabel,
        evidenceRefs: step.evidenceRefs,
        sourceState: 'split',
        sourceStepIds: step.sourceStepIds,
      };
      next.orderedStepIds = replaceIds(currentIds, [operation.stepId], [firstId, secondId]);
      return withHistory(next, operation);
    }

    case 'merge': {
      const firstIndex = currentIds.indexOf(operation.stepId);
      const secondIndex = currentIds.indexOf(operation.withStepId);
      if (firstIndex < 0 || secondIndex < 0 || Math.abs(firstIndex - secondIndex) !== 1) {
        return overlay;
      }
      const startIndex = Math.min(firstIndex, secondIndex);
      const firstStep = currentSteps[startIndex];
      const secondStep = currentSteps[startIndex + 1];
      const mergedLabel =
        operation.label || `${firstStep.label} and ${secondStep.label}`;
      const next = cloneOverlay(overlay);
      const mergedId = uniqueStepId(
        `merge-${firstStep.id}-${secondStep.id}`,
        [...currentIds, ...Object.keys(next.manualSteps)],
      );
      next.hiddenStepIds = addUnique(
        addUnique(next.hiddenStepIds, firstStep.id),
        secondStep.id,
      );
      next.manualSteps[mergedId] = {
        id: mergedId,
        label: mergedLabel,
        evidenceRefs: uniqueEvidenceRefs([
          ...firstStep.evidenceRefs,
          ...secondStep.evidenceRefs,
        ]),
        sourceState: 'merged',
        sourceStepIds: addUnique(firstStep.sourceStepIds, secondStep.sourceStepIds),
      };
      next.orderedStepIds = [
        ...currentIds.slice(0, startIndex),
        mergedId,
        ...currentIds.slice(startIndex + 2),
      ];
      return withHistory(next, operation);
    }

    default:
      return overlay;
  }
}

function resolveBusinessSequenceFromSteps(
  derivedSteps: BusinessSequenceStep[],
  overlayInput: unknown,
): BusinessSequenceStep[] {
  const overlay = normalizeSequenceOverlay(overlayInput);
  const hidden = new Set(overlay.hiddenStepIds);
  const stepsById = new Map<string, BusinessSequenceStep>();

  for (const step of derivedSteps) {
    if (hidden.has(step.id)) continue;
    const override = overlay.labelOverrides[step.id];
    const evidenceRefs = step.evidenceRefs.slice();
    stepsById.set(step.id, {
      ...step,
      label: override || step.label,
      evidenceRefs,
      evidenceState: evidenceRefs.length > 0 ? 'available' : 'missing',
      sourceState: override ? 'edited' : step.sourceState,
    });
  }

  for (const overlayStep of Object.values(overlay.manualSteps)) {
    if (hidden.has(overlayStep.id)) continue;
    const override = overlay.labelOverrides[overlayStep.id];
    const evidenceRefs = overlayStep.evidenceRefs.slice();
    stepsById.set(overlayStep.id, {
      id: overlayStep.id,
      label: override || overlayStep.label,
      order: 0,
      evidenceRefs,
      evidenceState: evidenceRefs.length > 0 ? 'available' : 'missing',
      sourceState: overlayStep.sourceState,
      sourceStepIds: overlayStep.sourceStepIds.slice(),
    });
  }

  for (const [id, label] of Object.entries(overlay.labelOverrides)) {
    if (stepsById.has(id) || hidden.has(id)) continue;
    stepsById.set(id, {
      id,
      label,
      order: 0,
      evidenceRefs: [],
      evidenceState: 'missing',
      sourceState: 'orphaned',
      sourceStepIds: [id],
    });
  }

  const defaultOrder = [
    ...derivedSteps.map((step) => step.id),
    ...Object.keys(overlay.manualSteps),
  ].filter((id) => stepsById.has(id));
  const orderedIds = uniqueIds([
    ...overlay.orderedStepIds.filter((id) => stepsById.has(id)),
    ...defaultOrder,
  ]);

  return orderedIds.map((id, index) => ({
    ...stepsById.get(id)!,
    order: index + 1,
  }));
}

function deriveEvidenceRefs(
  statement: Statement,
  allStatements: Statement[],
): EvidenceReference[] {
  const related = allStatements.filter(
    (candidate) =>
      candidate.id === statement.id
      || (candidate.type === 'event' && isRelatedEvent(candidate, statement.id)),
  );

  return uniqueEvidenceRefs(
    related
      .sort((a, b) => a.id - b.id)
      .map((candidate) => ({
        id: `statement-${candidate.id}`,
        statementId: candidate.id,
        summary: summarizeStatement(candidate),
        source: candidate.source,
      })),
  );
}

function isRelatedEvent(statement: Statement, sourceStatementId: number): boolean {
  if (statement.triggeredBy?.kind === 'from') {
    return statement.triggeredBy.actionId === sourceStatementId;
  }
  if (statement.triggeredBy?.kind === 'on') {
    return triggerRefs(statement.triggeredBy.expr).includes(sourceStatementId);
  }
  return false;
}

function triggerRefs(expr: TriggerExpr): number[] {
  if ('ref' in expr) return [expr.ref];
  return [...triggerRefs(expr.left), ...triggerRefs(expr.right)];
}

function summarizeStatement(statement: Statement): string {
  const actor = statement.actor?.name || 'Captured step';
  return `${actor}: ${humanizeActivity(statement.name)}`;
}

function stepIdForStatement(statementId: number): string {
  return `flow-${statementId}`;
}

function humanizeActivity(value: string): string {
  const normalized = value
    .replace(/[_-]+/g, ' ')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return 'Untitled step';
  const lower = normalized.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function cleanLabel(label: string): string {
  return label.replace(/\s+/g, ' ').trim();
}

function normalizeStringRecord(input: unknown): Record<string, string> {
  if (!input || typeof input !== 'object') return {};
  const output: Record<string, string> = {};
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (isNonEmptyString(key) && typeof value === 'string' && value.trim()) {
      output[key] = cleanLabel(value);
    }
  }
  return output;
}

function normalizeOverlaySteps(input: unknown): Record<string, SequenceOverlayStep> {
  if (!input || typeof input !== 'object') return {};
  const output: Record<string, SequenceOverlayStep> = {};
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (!isNonEmptyString(key) || !value || typeof value !== 'object') continue;
    const raw = value as Partial<SequenceOverlayStep>;
    const label = typeof raw.label === 'string' ? cleanLabel(raw.label) : '';
    if (!label) continue;
    const sourceState =
      raw.sourceState === 'split' || raw.sourceState === 'merged'
        ? raw.sourceState
        : 'manual';
    output[key] = {
      id: key,
      label,
      evidenceRefs: Array.isArray(raw.evidenceRefs)
        ? raw.evidenceRefs.filter(isEvidenceReference)
        : [],
      sourceState,
      sourceStepIds: Array.isArray(raw.sourceStepIds)
        ? raw.sourceStepIds.filter(isNonEmptyString)
        : [],
    };
  }
  return output;
}

function isEvidenceReference(value: unknown): value is EvidenceReference {
  if (!value || typeof value !== 'object') return false;
  const ref = value as EvidenceReference;
  return (
    typeof ref.id === 'string'
    && (typeof ref.statementId === 'number' || ref.statementId === null)
    && typeof ref.summary === 'string'
    && typeof ref.source === 'string'
  );
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function cloneOverlay(overlay: StreamBusinessSequenceOverlay): StreamBusinessSequenceOverlay {
  return {
    version: 1,
    orderedStepIds: overlay.orderedStepIds.slice(),
    labelOverrides: { ...overlay.labelOverrides },
    hiddenStepIds: overlay.hiddenStepIds.slice(),
    manualSteps: Object.fromEntries(
      Object.entries(overlay.manualSteps).map(([id, step]) => [
        id,
        {
          ...step,
          evidenceRefs: step.evidenceRefs.slice(),
          sourceStepIds: step.sourceStepIds.slice(),
        },
      ]),
    ),
    operationHistory: overlay.operationHistory.slice(),
  };
}

function withHistory(
  overlay: StreamBusinessSequenceOverlay,
  operation: SequenceOperation,
): StreamBusinessSequenceOverlay {
  return {
    ...overlay,
    operationHistory: [...overlay.operationHistory, operation],
  };
}

function resolveInsertIndex(
  operation: { toIndex?: number; beforeStepId?: string; afterStepId?: string },
  ids: string[],
): number {
  if (typeof operation.toIndex === 'number') {
    return clampIndex(operation.toIndex, ids.length);
  }
  if (operation.beforeStepId) {
    const beforeIndex = ids.indexOf(operation.beforeStepId);
    if (beforeIndex >= 0) return beforeIndex;
  }
  if (operation.afterStepId) {
    const afterIndex = ids.indexOf(operation.afterStepId);
    if (afterIndex >= 0) return afterIndex + 1;
  }
  return ids.length;
}

function clampIndex(index: number, max: number): number {
  if (index < 0) return 0;
  if (index > max) return max;
  return index;
}

function insertAt(ids: string[], id: string, index: number): string[] {
  return [...ids.slice(0, index), id, ...ids.slice(index)];
}

function replaceIds(ids: string[], removeIds: string[], insertIds: string[]): string[] {
  const removeSet = new Set(removeIds);
  const output: string[] = [];
  for (const id of ids) {
    if (!removeSet.has(id)) {
      output.push(id);
      continue;
    }
    if (removeIds[0] === id) output.push(...insertIds);
  }
  return output;
}

function uniqueIds(ids: string[]): string[] {
  return addUnique([], ids);
}

function addUnique<T>(items: T[], itemOrItems: T | T[]): T[] {
  const next = items.slice();
  const values = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
  for (const value of values) {
    if (!next.includes(value)) next.push(value);
  }
  return next;
}

function uniqueEvidenceRefs(refs: EvidenceReference[]): EvidenceReference[] {
  const seen = new Set<string>();
  const output: EvidenceReference[] = [];
  for (const ref of refs) {
    const key = ref.statementId === null ? ref.id : `statement-${ref.statementId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    output.push(ref);
  }
  return output;
}

function uniqueStepId(base: string, existingIds: string[]): string {
  const existing = new Set(existingIds);
  if (!existing.has(base)) return base;
  let index = 2;
  while (existing.has(`${base}-${index}`)) index += 1;
  return `${base}-${index}`;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
